/**
 * NOLAS 2026 — © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/i18n/fr.transport.ts
 *
 * Rôle :
 *   Sous-catalogue i18n du chantier « transport ». Fusionné dans `messagesFr`
 *   par `i18n/fr.ts`, donc utilisable exactement comme n'importe quelle clé.
 *
 * Pourquoi un fichier SÉPARÉ plutôt que d'allonger `fr.ts` :
 *   `fr.ts` fait ~6 700 lignes et c'est LA dépendance partagée du dépôt.
 *   Plusieurs chantiers menés en parallèle qui y écrivent en même temps se
 *   clobberaient — leçon du lot Console C9 : les agents parallèles ne se
 *   gênent pas sur LEURS fichiers, ils se gênent sur les fichiers COMMUNS.
 *   Un catalogue par chantier supprime la collision par construction.
 *
 * ⚠ Règles :
 *   • Wording français strict (§15.5), apostrophe typographique `’`.
 *   • JAMAIS le mot « atelier » en dur : employer les tokens du lexique
 *     (`{lexWorkshopPlural}`…) — cf. utils/eventLexicon.ts. Un test échoue
 *     sinon.
 *   • Aucune apostrophe DROITE juste avant un `{token}` (échappement ICU).
 *
 * @author Mikael
 * @since  2026-08-03 (lot CIRFA)
 */

export const messagesTransportFr: Record<string, string> = {
  // ────────────────────────────────────────────────────────────────────────
  // Vocabulaire commun
  // ────────────────────────────────────────────────────────────────────────
  'transport.direction.aller': 'Aller',
  'transport.direction.retour': 'Retour',

  // ── Feuille de route imprimable ─────────────────────────────────────────
  'transport.roadmap.title': 'Feuille de route',
  'transport.roadmap.back': '← Retour au plan de transport',
  'transport.roadmap.print': 'Imprimer',
  'transport.roadmap.loading': 'Chargement de la feuille de route…',
  'transport.roadmap.error':
    'Impossible de charger cette feuille de route. Réessayez dans quelques instants.',
  'transport.roadmap.badId': 'Cette rotation n’existe pas.',
  'transport.roadmap.empty':
    'Aucun passager n’est encore affecté à cette rotation. Affectez un groupe depuis le plan de transport, puis synchronisez les passagers.',
  'transport.roadmap.headcount':
    '{total, plural, =0 {Aucun passager attendu} one {1 passager attendu} other {# passagers attendus}} · {groups, plural, one {1 établissement} other {# établissements}}',
  'transport.roadmap.alreadyBoarded':
    '— {boarded, plural, one {1 déjà embarqué} other {# déjà embarqués}}',
  'transport.roadmap.groupCount':
    '({count, plural, one {1 personne} other {# personnes}})',
  'transport.roadmap.capacityValue':
    '{capacity, plural, one {1 place} other {# places}}',
  'transport.roadmap.accompanist': '(accompagnateur)',
  'transport.roadmap.col.check': 'Vu',
  'transport.roadmap.col.name': 'Nom et prénom',
  'transport.roadmap.col.seat': 'Place',
  'transport.roadmap.col.state': 'État',
  'transport.roadmap.signature.driver': 'Signature du chauffeur',
  'transport.roadmap.signature.escort': 'Signature de l’accompagnateur',

  'transport.rotationState.draft': 'Brouillon',
  'transport.rotationState.empty': 'Sans passager',
  'transport.rotationState.ready': 'Prête',
  'transport.rotationState.boarding': 'Embarquement en cours',
  'transport.rotationState.closed': 'Appel terminé',

  'transport.passengerState.pending': 'À pointer',
  'transport.passengerState.boarded': 'Monté',
  'transport.passengerState.no_show': 'Absent',

  // ────────────────────────────────────────────────────────────────────────
  // Écran « Plan de transport » (organisateur)
  // ────────────────────────────────────────────────────────────────────────
  'transport.admin.title': 'Plan de transport',
  'transport.admin.subtitle':
    'Rotations de véhicules, affectation des groupes et feuilles de route pour amener {lexVisitorDefPlural} sur site.',
  'transport.admin.loading': 'Chargement du plan de transport…',
  'transport.admin.error':
    'Impossible de charger le plan de transport. Vérifiez votre connexion puis réessayez.',
  'transport.admin.retry': 'Réessayer',
  'transport.admin.moduleOff':
    'Le module transport est désactivé pour cet événement. Vous pouvez préparer le plan dès maintenant : rien ne sera visible des accompagnateurs tant que le réglage « transport.enabled » reste à zéro.',
  'transport.admin.notShared':
    'Le plan est actif mais non partagé : les accompagnateurs ne voient pas encore leur transport (réglage « transport.public_display »).',

  'transport.admin.filter.legend': 'Filtrer par sens',
  'transport.admin.filter.all': 'Tous les sens',
  'transport.admin.btn.new': 'Nouvelle rotation',
  'transport.admin.btn.export': 'Exporter le plan (CSV)',
  'transport.admin.empty':
    'Aucune rotation pour le moment. Créez la première pour commencer à bâtir le plan.',
  'transport.admin.emptyFiltered':
    'Aucune rotation dans ce sens. Changez de filtre ou créez une rotation.',
  'transport.admin.count':
    '{count, plural, one {# rotation} other {# rotations}}',

  // ── Carte d’une rotation ────────────────────────────────────────────────
  'transport.card.departure': 'Départ',
  'transport.card.arrival': 'Arrivée',
  'transport.card.pickup': 'Point de montée',
  'transport.card.dropoff': 'Point de descente',
  'transport.card.carrier': 'Transporteur',
  'transport.card.vehicle': 'Véhicule',
  'transport.card.driver': 'Chauffeur',
  'transport.card.phone': 'Téléphone',
  'transport.card.notSet': 'Non renseigné',
  'transport.card.fill': '{assigned} / {capacity} places · {percent} %',
  'transport.card.fillNoLimit':
    '{assigned, plural, one {# personne affectée} other {# personnes affectées}} · jauge non renseignée',
  'transport.card.full': 'Complet',
  'transport.card.over': 'Jauge dépassée',
  'transport.card.remaining':
    '{remaining, plural, one {# place restante} other {# places restantes}}',
  'transport.card.groups': 'Groupes affectés',
  'transport.card.noGroups': 'Aucun groupe affecté à cette rotation.',
  'transport.card.groupLine': '{name} — {headcount} pers.',
  'transport.card.groupLineUnknown': '{name} — effectif à préciser',
  'transport.card.boarding':
    '{boarded} montés · {noShow} absents · {pending} à pointer',
  'transport.card.noPassengers':
    'Aucun passager nominatif. Synchronisez depuis les listes nominatives.',

  'transport.btn.roadmap': 'Feuille de route',
  'transport.btn.edit': 'Modifier',
  'transport.btn.delete': 'Supprimer',
  'transport.btn.assign': 'Affecter un groupe',
  'transport.btn.unassign': 'Retirer',
  'transport.btn.sync': 'Synchroniser les passagers',
  'transport.btn.cancel': 'Annuler',
  'transport.btn.save': 'Enregistrer',
  'transport.btn.confirm': 'Confirmer',

  // ── Formulaire de rotation ──────────────────────────────────────────────
  'transport.form.createTitle': 'Nouvelle rotation',
  'transport.form.editTitle': 'Modifier la rotation',
  'transport.form.label': 'Libellé',
  'transport.form.labelPlaceholder': 'Bus 1 — Lycée Hélène Boucher',
  'transport.form.direction': 'Sens',
  'transport.form.scheduledAt': 'Heure de départ',
  'transport.form.arrivalAt': 'Heure d’arrivée',
  'transport.form.pickup': 'Point de montée',
  // Exemple GÉNÉRIQUE : ce catalogue est celui de la plateforme, pas celui du
  // CIRFA. Un placeholder « Bd du XXᵉ Corps, Thionville » réintroduisait une
  // identité d'événement en dur — exactement ce que les lots 1 à 4 ont retiré,
  // et ce que le garde-fou `no-hardcoded-identity` interdit.
  'transport.form.pickupPlaceholder': 'Ex. : parvis de l’établissement',
  'transport.form.dropoff': 'Point de descente',
  'transport.form.capacity': 'Capacité (places)',
  'transport.form.capacityHelp':
    'Laissez vide si la jauge du véhicule est inconnue : aucune limite ne sera appliquée. Ne mettez zéro que pour bloquer volontairement toute affectation.',
  'transport.form.carrier': 'Transporteur',
  'transport.form.vehicle': 'Immatriculation ou numéro de car',
  'transport.form.driver': 'Nom du chauffeur',
  'transport.form.phone': 'Téléphone du chauffeur',
  'transport.form.notes': 'Consignes',
  'transport.form.notesHelp':
    'Visible sur la feuille de route imprimée et par les accompagnateurs.',
  'transport.form.published': 'Publier auprès des accompagnateurs',
  'transport.form.publishedHelp':
    'Tant que la rotation reste en brouillon, elle n’apparaît nulle part côté accompagnateur.',
  'transport.form.labelRequired': 'Le libellé est obligatoire.',

  // ── Affectation d’un groupe ─────────────────────────────────────────────
  'transport.assign.title': 'Affecter un groupe',
  'transport.assign.group': 'Groupe',
  'transport.assign.groupPlaceholder': 'Choisir un groupe…',
  'transport.assign.rosterHint':
    '{count, plural, =0 {aucun inscrit validé} one {# inscrit validé} other {# inscrits validés}}',
  'transport.assign.headcount': 'Effectif embarqué',
  'transport.assign.headcountHelp':
    'Laissez vide pour reprendre l’effectif validé de la liste nominative.',
  'transport.assign.sync': 'Générer aussitôt la liste nominative des passagers',
  'transport.assign.syncHelp':
    'Les inscrits validés du groupe deviennent passagers. Un émargement déjà fait n’est jamais écrasé.',
  'transport.assign.noGroups':
    'Aucun groupe n’est enregistré pour cet événement. Créez-en un dans « Groupes » avant d’affecter un transport.',
  'transport.assign.submit': 'Affecter',

  // ── Confirmations ───────────────────────────────────────────────────────
  'transport.confirm.deleteTitle': 'Supprimer la rotation',
  'transport.confirm.deleteBody':
    'La rotation « {label} », ses affectations de groupes et ses passagers seront supprimés. Cette action est définitive.',
  'transport.confirm.unassignTitle': 'Retirer le groupe',
  'transport.confirm.unassignBody':
    'Le groupe « {name} » ne voyagera plus sur cette rotation. Ses passagers non pointés seront retirés de la liste.',
  'transport.confirm.unassignForce':
    'Des passagers de ce groupe ont déjà été pointés. Les retirer effacera ces émargements. Confirmez-vous ?',

  // ── Retours utilisateur ─────────────────────────────────────────────────
  'transport.toast.created': 'Rotation créée.',
  'transport.toast.updated': 'Rotation mise à jour.',
  'transport.toast.deleted': 'Rotation supprimée.',
  'transport.toast.assigned': 'Groupe affecté à la rotation.',
  'transport.toast.unassigned': 'Groupe retiré de la rotation.',
  'transport.toast.synced':
    '{created, plural, =0 {Aucun nouveau passager} one {# passager ajouté} other {# passagers ajoutés}} · {total} au total.',
  'transport.toast.capacityExceeded':
    'Jauge du véhicule atteinte : cette affectation la dépasserait. Augmentez la capacité ou prévoyez un véhicule supplémentaire.',
  'transport.toast.boardingExists':
    'Des passagers de ce groupe ont déjà été pointés : confirmez pour retirer quand même.',
  'transport.toast.error': 'Action impossible : {message}',

  // ────────────────────────────────────────────────────────────────────────
  // Feuille de route + émargement
  // ────────────────────────────────────────────────────────────────────────
  'transportRoadmap.documentTitle': 'Feuille de route',
  'transportRoadmap.loading': 'Chargement de la feuille de route…',
  'transportRoadmap.error':
    'Impossible de charger la feuille de route. Vérifiez votre connexion puis réessayez.',
  'transportRoadmap.notFound': 'Cette rotation n’existe pas ou a été supprimée.',
  'transportRoadmap.btn.print': 'Imprimer / Enregistrer en PDF',
  'transportRoadmap.btn.back': 'Retour au plan',
  'transportRoadmap.btn.refresh': 'Actualiser',

  'transportRoadmap.field.direction': 'Sens',
  'transportRoadmap.field.departure': 'Départ',
  'transportRoadmap.field.arrival': 'Arrivée',
  'transportRoadmap.field.pickup': 'Montée',
  'transportRoadmap.field.dropoff': 'Descente',
  'transportRoadmap.field.carrier': 'Transporteur',
  'transportRoadmap.field.vehicle': 'Véhicule',
  'transportRoadmap.field.driver': 'Chauffeur',
  'transportRoadmap.field.phone': 'Téléphone',
  'transportRoadmap.field.capacity': 'Capacité',
  'transportRoadmap.field.capacityUnknown': 'non renseignée',
  'transportRoadmap.field.notSet': '—',
  'transportRoadmap.notes': 'Consignes',

  'transportRoadmap.expected':
    '{total, plural, one {# passager attendu} other {# passagers attendus}}',
  'transportRoadmap.progress': '{boarded} / {total} montés',
  'transportRoadmap.pending':
    '{pending, plural, =0 {personne à pointer} one {# personne à pointer} other {# personnes à pointer}}',
  'transportRoadmap.allAccounted':
    'Tout le monde est compté : le véhicule peut partir.',
  'transportRoadmap.empty':
    'Aucun passager sur cette rotation. Affectez un groupe puis synchronisez la liste nominative.',
  'transportRoadmap.groupCount':
    '{boarded} / {total} montés · {noShow} absents',
  'transportRoadmap.accompanist': 'Accompagnateur',
  'transportRoadmap.seat': 'Place {seat}',
  'transportRoadmap.checkboxHint': 'Cochez à la montée',
  'transportRoadmap.footer':
    'Document généré le {when} — à conserver par le chauffeur pendant tout le trajet.',
  'transportRoadmap.printedAt': 'Édité le {when}',

  'transportRoadmap.board.mark': 'Pointer la montée',
  'transportRoadmap.board.unmark': 'Annuler la montée',
  'transportRoadmap.board.noShow': 'Signaler absent',
  'transportRoadmap.board.cancelNoShow': 'Annuler l’absence',
  'transportRoadmap.board.queued':
    'Pointage enregistré hors ligne : il partira dès le retour du réseau.',
  'transportRoadmap.board.failed':
    'Pointage non enregistré : {message}. Réessayez.',
  'transportRoadmap.board.offlineBanner':
    'Réseau indisponible. Les pointages sont conservés sur l’appareil et envoyés automatiquement dès le retour de la connexion.',
};
